import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ItemList, File, DjangoListResponse, FileType } from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import {
  FolderSelectedState,
  IPath,
  TFolderGroup,
  buildPathTree,
  getFileTreeGroupedOptions,
} from './getFileTree';
import { useCurrentItemList } from 'v2/context/CurrentItemList';
import { computeFolderSelectionStateRec } from './getFolderState';
import { folderClickHandler } from './folderClickHandler';

export interface IUseFolderOptionsProps {
  defaultValues?: ItemList;
  dataVendorId?: number;
  fileTypes: FileType[];
}

const DefaultItemList: ItemList = {
  is_all: false,
  list: [],
  view_all: false,
};

/**
 * exported for tsesting Only, use useFolderTree instead
 */
export const useFolderTreeOptions = ({
  defaultValues = DefaultItemList,
  dataVendorId,
  fileTypes,
}: IUseFolderOptionsProps) => {
  const {
    setCurrentState: setCurrentSelection,
    currentState: currentSelection,
  } = useCurrentItemList();

  const [defaultOptions, setDefaultOptions] = useState<TFolderGroup<File>[]>(
    []
  );

  const [treeRoot, setTreeRoot] = useState<IPath | undefined>();

  const loadOptions = useCallback(
    async (
      inputValue: string,
      callback: (options: TFolderGroup<File>[]) => void
    ) => {
      const params = {
        preprocess_status: 'complete',
        relationships__table_name: 'DataVendor',
        relationships__object_id: (dataVendorId ?? '').toString(),
        ordering: '-timestamp',
        type: fileTypes,
        file: inputValue || undefined,
        size: !inputValue ? 500 : undefined,
      };
      try {
        const data = await restFactory.get<DjangoListResponse<File>>(
          `/dj/api/v1/file/`,
          params
        );
        const root = buildPathTree(data.results);
        const groups = getFileTreeGroupedOptions(root);
        setTreeRoot(root);
        callback(groups);
      } catch (error) {
        callback([]);
      }
    },
    [dataVendorId, fileTypes]
  );

  // trigger
  useEffect(() => {
    if (typeof dataVendorId == 'number') {
      loadOptions('', (options) => setDefaultOptions(options));
    } else {
      setCurrentSelection(defaultValues);
    }
  }, [dataVendorId, defaultValues, loadOptions, setCurrentSelection]);

  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(
    new Set<string>()
  );

  const toggleFolderCollapse = useCallback(
    (folder: string) => {
      if (!collapsedFolders.has(folder)) {
        collapsedFolders.add(folder);
      } else {
        collapsedFolders.delete(folder);
      }
      const updated = new Set(collapsedFolders.values());
      setCollapsedFolders(updated);
    },
    [collapsedFolders]
  );

  const handleFolderClick = useCallback(
    (path: string) => {
      const newSelection = folderClickHandler(treeRoot, path, currentSelection);
      if (newSelection !== currentSelection) {
        setCurrentSelection(newSelection);
      }
    },
    [currentSelection, setCurrentSelection, treeRoot]
  );

  const folderSelectionState = useMemo(() => {
    const result = new Map<string, keyof typeof FolderSelectedState>();
    computeFolderSelectionStateRec(treeRoot, currentSelection, result);
    return result;
  }, [currentSelection, treeRoot]);

  return {
    defaultOptions,
    defaultValues,
    collapsedFolders,
    folderSelectionState,
    loadOptions,
    toggleFolderCollapse,
    handleFolderClick,
  };
};

const FolderTreeOptionsContext = React.createContext<
  ReturnType<typeof useFolderTreeOptions>
>({
  defaultOptions: [],
  defaultValues: { is_all: true, list: [], view_all: false },
  collapsedFolders: new Set<string>(),
  folderSelectionState: new Map(),
  loadOptions: () => Promise.reject(),
  toggleFolderCollapse: () => {
    /** */
  },
  handleFolderClick: () => {
    /** */
  },
});

interface FolderTreeProviderProps extends IUseFolderOptionsProps {
  children: React.ReactNode;
}

export const CurrentFileTree = ({
  children,
  ...rest
}: FolderTreeProviderProps) => {
  const value = useFolderTreeOptions({ ...rest });
  return (
    <FolderTreeOptionsContext.Provider value={value}>
      {children}
    </FolderTreeOptionsContext.Provider>
  );
};

export const useCurrentFileTree = () => {
  return React.useContext(FolderTreeOptionsContext);
};
